import * as React from 'react';
import { Paper, Grid, Typography } from '@mui/material';

import DateFnsAdapter from '@date-io/date-fns';
import {
    chunks,
    getDaysInMonth,
    isStartOfRange,
    isEndOfRange,
    inDateRange,
    isRangeSameDay,
} from '../utils';
import Header from './Header';
import Day from './Day';

// eslint-disable-next-line no-unused-vars
import { NavigationAction, DateRange } from '../types';

const dateFns = new DateFnsAdapter();

const { toJsDate, isSameMonth, isSameDay, formatByString, isWithinRange } =
    dateFns;

const WEEK_DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const getDate = <DateType extends Date>(date: DateType): number => {
    const dateData = toJsDate(date);
    const dayOfMonth = dateData.getDate();
    return dayOfMonth;
};
const isToday = <DateType extends Date>(date: DateType): boolean =>
    isSameDay(date, new Date());

interface MonthProps {
    value: Date;
    marker: symbol;
    dateRange: DateRange;
    minDate: Date;
    maxDate: Date;
    navState: [boolean, boolean];
    setValue: (date: Date) => void;
    helpers: {
        inHoverRange: (day: Date) => boolean;
    };
    handlers: {
        onDayClick: (day: Date) => void;
        onDayHover: (day: Date) => void;
        onMonthNavigate: (marker: symbol, action: NavigationAction) => void;
    };
}

const Month: React.FunctionComponent<MonthProps> = (props: MonthProps) => {
    const {
        helpers,
        handlers,
        value: date,
        dateRange,
        marker,
        setValue: setDate,
        minDate,
        maxDate,
    } = props;

    // eslint-disable-next-line react/destructuring-assignment
    const [back, forward] = props.navState;

    return (
        <Paper square elevation={0} className="root-daysContainer">
            <Grid container className="container-daterangepicker">
                <Header
                    date={date}
                    setDate={setDate}
                    nextDisabled={!forward}
                    prevDisabled={!back}
                    onClickPrevious={() =>
                        handlers.onMonthNavigate(
                            marker,
                            NavigationAction.Previous
                        )
                    }
                    onClickNext={() =>
                        handlers.onMonthNavigate(marker, NavigationAction.Next)
                    }
                />

                <Grid
                    item
                    container
                    direction="row"
                    justifyContent="space-between"
                    className="weekDaysContainer"
                >
                    {WEEK_DAYS.map((day) => (
                        <Typography
                            color="textSecondary"
                            key={day}
                            variant="caption"
                            className="daylabel"
                        >
                            {day}
                        </Typography>
                    ))}
                </Grid>

                <Grid
                    item
                    container
                    direction="column"
                    justifyContent="space-between"
                    className="daysContainer"
                >
                    {chunks(getDaysInMonth(date), 7).map((week, idx) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Grid
                            key={idx}
                            container
                            direction="row"
                            justifyContent="center"
                        >
                            {week.map((day) => {
                                const isStart = isStartOfRange(dateRange, day);
                                const isEnd = isEndOfRange(dateRange, day);
                                const isRangeOneDay = isRangeSameDay(dateRange);
                                const highlighted =
                                    inDateRange(dateRange, day) ||
                                    helpers.inHoverRange(day);

                                return (
                                    <Day
                                        key={formatByString(day, 'MM-dd-yyyy')}
                                        filled={isStart || isEnd}
                                        outlined={isToday(day)}
                                        highlighted={
                                            highlighted && !isRangeOneDay
                                        }
                                        disabled={
                                            !isSameMonth(date, day) ||
                                            !isWithinRange(day, [
                                                minDate,
                                                maxDate,
                                            ])
                                        }
                                        startOfRange={isStart && !isRangeOneDay}
                                        endOfRange={isEnd && !isRangeOneDay}
                                        onClick={() => handlers.onDayClick(day)}
                                        onHover={() => handlers.onDayHover(day)}
                                        value={getDate(day)}
                                    />
                                );
                            })}
                        </Grid>
                    ))}
                </Grid>
            </Grid>
        </Paper>
    );
};

export default Month;
